import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FormContainer from "../../containers/FormContainer";
import TextBox from "../shared/TextBox";
import SelectBox from "../shared/SelectBox";
import SubmitButton from "../shared/SubmitButton";
import { useModal } from "../modal/ModalContext";
import { createLinkedInAccount, getLinkedInAccountById, updateLinkedInAccount } from "../../services/apiService";
import { unwrapAccountPayload } from "./proxyReady";
import { getAccountLimits } from "./accountState";
import { getErrorMessage, notifyError } from "../../helpers/apiError";

// List rows are a slim projection; prefill from the full record so saving doesn't drop fields.
const toForm = (account) => {
  const limits = getAccountLimits(account);
  return {
    accountName: account.accountName || "",
    email: account.email || "",
    password: "",
    profileUrl: account.profileUrl || "",
    firstName: account.firstName || "",
    lastName: account.lastName || "",
    headline: account.headline || "",
    company: account.company || "",
    location: account.location || "",
    industry: account.industry || "",
    status: account.status || "ACTIVE",
    proxyHost: account.proxyHost || "",
    proxyPort: account.proxyPort || "",
    proxyUsername: account.proxyUsername || "",
    proxyPassword: "", // never prefilled; API no longer returns it and resending ciphertext breaks login
    proxyCountry: account.proxyCountry || "",
    proxyType: account.proxyType || "RESIDENTIAL",
    dailyConnectionLimit: limits.connectionsTotal || "",
    dailyMessageLimit: limits.messagesTotal || "",
    connectionDelay: account.connectionDelay || "",
  };
};

const statusOptions = [
  { value: "ACTIVE", label: "status.ACTIVE" },
  { value: "INACTIVE", label: "status.INACTIVE" },
  { value: "SUSPENDED", label: "status.SUSPENDED" },
  { value: "BANNED", label: "status.BANNED" },
  { value: "PENDING", label: "status.PENDING" },
  { value: "VERIFICATION", label: "status.VERIFICATION" },
];

const proxyTypeOptions = [
  { value: "RESIDENTIAL", label: "RESIDENTIAL" },
  { value: "DATACENTER", label: "DATACENTER" },
  { value: "MOBILE", label: "MOBILE" },
];

const LinkedInAccountModal = ({ account, onSuccess }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  const isEditing = !!account;

  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showProxy, setShowProxy] = useState(false);

  const [form, setForm] = useState({
    accountName: "",
    email: "",
    password: "",
    profileUrl: "",
    firstName: "",
    lastName: "",
    headline: "",
    company: "",
    location: "",
    industry: "",
    status: "ACTIVE",
    proxyHost: "",
    proxyPort: "",
    proxyUsername: "",
    proxyPassword: "",
    proxyCountry: "",
    proxyType: "RESIDENTIAL",
    dailyConnectionLimit: "",
    dailyMessageLimit: "",
    connectionDelay: "",
  });

  const translatedStatusOptions = statusOptions.map((opt) => ({
    ...opt,
    label: t(opt.label),
  }));

  useEffect(() => {
    if (!account) return undefined;
    let cancelled = false;
    const apply = (source) => {
      setForm(toForm(source));
      if (source.proxyHost) setShowProxy(true);
    };
    apply(account);
    if (account.id) {
      setIsLoading(true);
      getLinkedInAccountById(account.id)
        .then((response) => {
          const full = unwrapAccountPayload(response);
          if (!cancelled && full?.id) apply({ ...account, ...full });
        })
        .catch(() => {})
        .finally(() => !cancelled && setIsLoading(false));
    }
    return () => {
      cancelled = true;
    };
  }, [account]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Proxy must always follow the country the user chose — never left blank.
  const isDisabled =
    !form.accountName ||
    !form.email ||
    !form.proxyCountry?.trim() ||
    (!isEditing && !form.password);

  const handleSubmit = async () => {
    setIsLoading(true);
    setFormError(null);

    const payload = { ...form };

    // Convert numeric fields
    if (payload.proxyPort) payload.proxyPort = parseInt(payload.proxyPort, 10);
    if (payload.dailyConnectionLimit) payload.dailyConnectionLimit = parseInt(payload.dailyConnectionLimit, 10);
    if (payload.dailyMessageLimit) payload.dailyMessageLimit = parseInt(payload.dailyMessageLimit, 10);
    if (payload.connectionDelay) payload.connectionDelay = parseInt(payload.connectionDelay, 10);

    // Remove empty proxy host fields if advanced proxy not shown — keep proxyCountry
    if (!showProxy) {
      delete payload.proxyHost;
      delete payload.proxyPort;
      delete payload.proxyUsername;
      delete payload.proxyPassword;
      delete payload.proxyType;
    }

    // Remove empty password when editing
    if (isEditing && !payload.password) delete payload.password;

    // Remove empty optional fields
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    try {
      if (isEditing) {
        await updateLinkedInAccount(account.id, payload);
        toast.success(t("linkedin.toast.updateSuccess"));
      } else {
        await createLinkedInAccount(payload);
        toast.success(t("linkedin.toast.createSuccess"));
      }
      hideModal();
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = getErrorMessage(err, t("linkedin.toast.error"));
      setFormError(Array.isArray(msg) ? msg : msg);
      notifyError(err, t("linkedin.toast.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-[40px] max-h-[80vh] overflow-y-auto">
      <FormContainer
        isLoading={isLoading}
        error={typeof formError === "string" || Array.isArray(formError) ? formError : null}
        onSubmit={handleSubmit}
      >
        {/* Basic Information */}
        <h3 className="text-[14px] font-bold text-taction mb-4 uppercase tracking-wide border-b pb-2">
          {t("linkedin.basicInfo")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <TextBox
            label={t("linkedin.accountName")}
            name="accountName"
            value={form.accountName}
            onChange={handleChange}
            placeholder={t("linkedin.accountName_placeholder")}
            error={formError?.accountName}
          />
          <TextBox
            label={t("linkedin.email")}
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder={t("linkedin.email_placeholder")}
            error={formError?.email}
          />
          <TextBox
            label={t("linkedin.password")}
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            placeholder={isEditing ? "Leave blank to keep current" : t("linkedin.password_placeholder")}
            error={formError?.password}
          />
          <TextBox
            label={t("linkedin.profileUrl")}
            name="profileUrl"
            value={form.profileUrl}
            onChange={handleChange}
            placeholder={t("linkedin.profileUrl_placeholder")}
            error={formError?.profileUrl}
          />
          <TextBox
            label={t("linkedin.firstName")}
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            placeholder={t("linkedin.firstName_placeholder")}
          />
          <TextBox
            label={t("linkedin.lastName")}
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            placeholder={t("linkedin.lastName_placeholder")}
          />
          <TextBox
            label={t("linkedin.headline")}
            name="headline"
            value={form.headline}
            onChange={handleChange}
            placeholder={t("linkedin.headline_placeholder")}
          />
          <TextBox
            label={t("linkedin.company")}
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder={t("linkedin.company_placeholder")}
          />
          <TextBox
            label={t("linkedin.location")}
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder={t("linkedin.location_placeholder")}
          />
          <TextBox
            label={t("linkedin.industry")}
            name="industry"
            value={form.industry}
            onChange={handleChange}
            placeholder={t("linkedin.industry")}
          />
          <SelectBox
            label={t("linkedin.status")}
            name="status"
            value={form.status}
            onChange={handleChange}
            options={translatedStatusOptions}
            placeholder={t("linkedin.status")}
            error={formError?.status}
          />
          <TextBox
            label={t("linkedin.dailyConnectionLimit")}
            name="dailyConnectionLimit"
            type="number"
            value={form.dailyConnectionLimit}
            onChange={handleChange}
            placeholder={t("linkedin.dailyConnectionLimit_placeholder")}
          />
          <TextBox
            label={t("linkedin.dailyMessageLimit")}
            name="dailyMessageLimit"
            type="number"
            value={form.dailyMessageLimit}
            onChange={handleChange}
            placeholder={t("linkedin.dailyMessageLimit_placeholder")}
          />
          <TextBox
            label={t("linkedin.connectionDelay")}
            name="connectionDelay"
            type="number"
            value={form.connectionDelay}
            onChange={handleChange}
            placeholder={t("linkedin.connectionDelay_placeholder")}
          />
        </div>

        <div className="mb-4">
          <TextBox
            label={t("linkedin.proxyCountry")}
            name="proxyCountry"
            value={form.proxyCountry}
            onChange={handleChange}
            placeholder={t("linkedin.proxyCountry_placeholder")}
          />
        </div>

        {/* Proxy Settings Toggle */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowProxy(!showProxy)}
            className="flex items-center gap-2 text-[14px] font-bold text-taction hover:underline focus:outline-none"
          >
            <span>{showProxy ? "▼" : "▶"}</span>
            {t("linkedin.proxyOptional")}
          </button>
        </div>

        {showProxy && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 border rounded-[8px] p-4 bg-gray-50 mb-4">
            <TextBox
              label={t("linkedin.proxyHost")}
              name="proxyHost"
              value={form.proxyHost}
              onChange={handleChange}
              placeholder={t("linkedin.proxyHost_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyPort")}
              name="proxyPort"
              type="number"
              value={form.proxyPort}
              onChange={handleChange}
              placeholder={t("linkedin.proxyPort_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyUsername")}
              name="proxyUsername"
              value={form.proxyUsername}
              onChange={handleChange}
              placeholder={t("linkedin.proxyUsername_placeholder")}
            />
            <TextBox
              label={t("linkedin.proxyPassword")}
              name="proxyPassword"
              type="password"
              value={form.proxyPassword}
              onChange={handleChange}
              placeholder={isEditing ? "Leave blank to keep current" : t("linkedin.proxyPassword_placeholder")}
            />
            <SelectBox
              label={t("linkedin.proxyType")}
              name="proxyType"
              value={form.proxyType}
              onChange={handleChange}
              options={proxyTypeOptions}
            />
          </div>
        )}

        <SubmitButton
          customStyle="inline"
          label={isEditing ? t("form.save") : t("linkedin.add")}
          disabled={isDisabled}
          isLoading={isLoading}
        />
      </FormContainer>
    </div>
  );
};

export default LinkedInAccountModal;
